﻿import { LiveDataSet } from "../../API/LiveData/LiveDataSet";
import { ILiveDataListener } from "../../ILiveDataListener";
import { InfluxDataWrapper } from "../../InfluxDb/InfluxDataWrapper";
import { getGroupData, getHistoricData } from "../../InfluxDb/InfluxDb";
import { MainSite, ITimeZoneData } from "../../MainSite";
import { ParameterGroupInfoSet } from "../../ParameterGroupInfoSet";
import { SimpleDialog } from "../../SimpleDialog";
import { DateRangePicker } from "../Helpers/DateRangePicker";
import { SpeedUnits } from "./Current";
import { ISiteObservationsPage } from "./ISiteObservationsPage";

interface PVDGradientEntry {
    speed: number;
    color: string;
}

interface PVDSpeedOptions {
    min: number;
    max: number;
    reversed: boolean;
    textGranularity: number;
}

interface PVDChartDataPoint {
    timestamp: number;
    speed: number;
    direction: number;
}

interface PVDChartOptions extends __Highstock.Options {
    //: having to extend default options to include boost module options (module introduced when adding Hovmoller charts, and it ended up affecting the PVD chart - got https://assets.highcharts.com/errors/12/ error on long date ranges with large data sets, as PVD data has multidimentional objects asvalues to carry colour as well as point coordinates & data)
    boost: {
        enabled: boolean;
    };
}

export class ProgressiveVectorDiagram implements ILiveDataListener {
    private chartContainer: JQuery;
    private chart: __Highcharts.ChartObject;
    private stationId: string;
    private box: JQuery;
    private legendContainer: JQuery;
    private selectedHours: number;
    private forecast: boolean;
    private drp: DateRangePicker;

    private $speedLegendUL: JQuery;
    private $ckboxScales: JQuery;
    private scaleMin: number = null;
    private scaleMax: number = null;
    private originalContainerHeightPercent: number;

    private lsOptsKey: string = "pvdOpts"; //: the key/name for cookie or local storage for user options for PVD chart.
    private lsSquareChartKey: string = "pvdSquareChart";

    constructor(
        private mainSite: MainSite,
        page: ISiteObservationsPage,
        container: JQuery,
        top: number,
        left: number,
        height: number,
        width: number,
        private currentSpeedUnits: SpeedUnits,
        private sensorId: string = null,
    ) {
        this.stationId = page.station.Station.Id;

        this.box = $('<div class="midi_SiteObservations_chart_container"></div>')
            .css("position", "absolute")
            .css("top", top + "%")
            .css("left", left + "%")
            .css("height", height + "%")
            .css("width", width + "%");

        container.append(this.box);
        let $chartToolbars = $(
            '<div class="current_chart_selector no-select row" style="font-size: 0.9em;"></div>',
        ).appendTo(this.box);

        let $dateRangeSelector = $(
            '<div class="col-sm-9" data-toolbar-type="dates"></div>',
        ).appendTo($chartToolbars);
        let timePeriodSelectorContainer = $(
            '<div class="col-sm-3" data-toolbar-type="periods"></div>',
        ).appendTo($chartToolbars);

        //: add DateRangePicker:
        this.drp = new DateRangePicker(this.mainSite, $dateRangeSelector, (drp) => {
            if (!drp.onChangeDateViaSetValuesMethods) {
                this.drp.dropdownSetButtonText("Custom range");
                this.selectedHours = null; //: important: we use this to determine if livedata should shift datepicker range..
            }

            let data = drp.getData();

            if (data && data.isValid && data.hasRange) {
                this.updateChart();
            }
        });
        this.drp.render(null, "row", true);
        //this.drp.setTimezone("UTC"); //: this chart would use UTC 'hours' buttons before adding date-range picker..

        $("<button>Fullscreen</button>")
            .appendTo($dateRangeSelector)
            .click(() => {
                (this.chart as any).fullscreen.open();
            });

        let predefinedPeriods = [-24, -168, -720, 120]; //: negative = past, positives = forecasts (NB: if forecast data not available, positives get removed automatically)

        //check that we have any forecast data before appending the forecast time period selector
        let fcstParam =
            "Forecast_" +
            (this.currentSpeedUnits == SpeedUnits.mps
                ? ParameterGroupInfoSet.Total_CurrentSpeed_Surface_mps_Mean.Id
                : ParameterGroupInfoSet.Total_CurrentSpeed_Surface_kts_Mean.Id);

        //see if we have any forecast data before showing the forecast block
        getGroupData(
            this.stationId,
            fcstParam,
            moment.utc().valueOf(),
            moment.utc().add("d", 1).valueOf(),
            (err, forecastData) => {
                if (forecastData.Data !== null && forecastData.Data.length > 0) {
                    predefinedPeriods = predefinedPeriods.filter((x) => x < 0); //: remove positives as they would be deemed as forecasts.
                }
            },
            this.sensorId ?? undefined,
        );

        predefinedPeriods.forEach((hours, i) => {
            let prev = i == 0 ? null : predefinedPeriods[i - 1];
            if (i == 0) {
                this.drp.dropdownAddItem("header", "Predefined periods", null);
                if (hours < 0) {
                    this.drp.dropdownAddItem("separator", null, null);
                    this.drp.dropdownAddItem("header", "Past", null);
                }
            }

            if (prev < 0 && hours >= 0) {
                this.drp.dropdownAddItem("separator", null, null);
                this.drp.dropdownAddItem("header", "Forecast", null);
            }

            let isForecast: boolean = hours > 0;
            let days: number = hours / 24;
            let label: string = `${Math.abs(days)} day${days == 1 || days == -1 ? "" : "s"}`;
            this.drp.dropdownAddItem(
                `predefined-hours-${hours}`,
                label,
                (drp) => {
                    this.selectedHours = Math.abs(hours);
                    this.forecast = isForecast;
                    this.updateChartHours(Math.abs(hours), true, isForecast);

                    let $el = $("<span>");
                    $el.append(
                        $(`<em class="text-primary">${isForecast ? "Forecast" : "Past"}</em>`).css(
                            { marginRight: 5 },
                        ),
                    );
                    $el.append($(`<span>${label}</span>`));
                    this.drp.dropdownSetButtonElements($el);
                },
                i == 0 ? "active" : "",
                isForecast ? "fa-forward" : "fa-backward",
            );
        });
        this.drp.dropdownSetButtonText("Select range");

        //: add a timezone indicator (tucked away in drop-down of predefined ranges)
        let tzd = this.drp.getSelectedTimezoneData();
        if (tzd) {
            if (predefinedPeriods.length > 0) {
                this.drp.dropdownAddItem("separator", null, null);
            }

            this.drp.dropdownAddItem(
                "timezone-display",
                `${tzd.timeZoneCode} (${tzd.offsetString})`,
                null,
                "disabled",
                "fa-globe",
            );
        }

        this.$ckboxScales = $('<input type="checkbox">').css({ cursor: "pointer" });

        this.$ckboxScales.prependTo(
            $("<label>")
                .css({ fontWeight: "normal", cursor: "pointer" })
                .text("Equal scales")
                .appendTo(
                    $("<div>")
                        .addClass("checkbox-inline pull-right")
                        .css({ marginTop: "4px" })
                        .disableSelection()
                        .appendTo(timePeriodSelectorContainer),
                ),
        );

        this.$ckboxScales.on("change", (e) => {
            if (this.$ckboxScales.prop("checked")) {
                $.cookie(this.lsSquareChartKey, "true", { expires: 5 * 365, path: "/" });
            } else {
                $.removeCookie(this.lsSquareChartKey);
            }

            this.setSquareChart(this.$ckboxScales.prop("checked"), true);
            this.autoSetChartAxes();
        });

        //: set square chart if appropriate
        let cookiePVDSquare: string = $.cookie(this.lsSquareChartKey);
        if (typeof cookiePVDSquare == "string" && cookiePVDSquare == "true") {
            this.$ckboxScales.prop("checked", true);
        }

        this.originalContainerHeightPercent = 83;
        this.chartContainer = $("<div></div>")
            .css("height", this.originalContainerHeightPercent + "%")
            .appendTo(this.box);

        $(window).on("resize", (e) => {
            this.setSquareChart(this.$ckboxScales.prop("checked"), true);
        });

        this.legendContainer = $(
            '<div class="midi_SiteObservations_chart_heatmapLegend"></div>',
        ).hide();
        this.$speedLegendUL = $("<ul></ul>").appendTo(this.legendContainer);

        this.setSpeedLegend(0, 0.8, true, 1); //: default speed legend as was previously set..

        let $legendTitle: JQuery = $('<div class="legendTitle"></div>');
        $("<span>").text("Current Speed (m/s)").appendTo($legendTitle);
        let $aSpeedOpts: JQuery = $("<a>")
            .text("Customise")
            .attr("href", "#")
            .css("marginLeft", "5px")
            .addClass("btn btn-default btn-xs")
            .appendTo($legendTitle);
        $aSpeedOpts.on("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.showSpeedOptionsModal();
            return false;
        });

        $legendTitle.appendTo(this.legendContainer);
        this.box.append(this.legendContainer);

        this.addChart();
        this.drp.dropdownTriggerItemClick(`predefined-hours-${predefinedPeriods[0]}`);
    }

    public liveDataChanged(latestData: LiveDataSet): void {
        if (!this.mainSite.liveDataManager.latestData) return;

        if (this.drp) {
            if (this.selectedHours !== null) {
                //: alternatively check if the previously set range contained NOW : this.drp.getLastIncludedNow()
                this.drp.autoIncludeNow("shift", true, "PVD.liveDataChanged()");
            } else if (this.drp.getCurrentRangeIncludesNow()) {
                //: if current selected range contains NOW, then simply update the chart (with data refetch), without modifying the range.
                this.updateChart();
            }
        }
    }

    private addChart(): void {
        this.chartContainer.css({ marginTop: 40 });

        let chartOptions: PVDChartOptions = {
            chart: {
                type: "scatter",
            },
            boost: {
                enabled: false,
            },
            exporting: {
                enabled: true,
            },
            title: {
                text: "",
            },
            xAxis: {
                title: {
                    text: "Virtual Distance E (km)",
                },
                startOnTick: false,
                endOnTick: false,
            },
            yAxis: {
                title: {
                    text: "Virtual Distance N (km)",
                },
                startOnTick: false,
                endOnTick: false,
            },
            legend: {
                enabled: false,
            },
            plotOptions: {
                line: {
                    marker: {
                        radius: 2,
                        enabled: true,
                    },
                },
                series: {
                    animation: false,
                    turboThreshold: 0,
                    tooltip: {
                        pointFormat:
                            "<b>{point.name}</b><br/>Virtual Distance E (km): <b>{point.x:.2f}</b><br/>Virtual Distance N (km): <b>{point.y:.2f}</b><br/>Speed: <b>{point.speed:.3f}</b>",
                    },
                    dataLabels: {
                        padding: 5,
                        allowOverlap: true,
                    },
                },
            },
            credits: {
                enabled: false,
            },
        };

        this.chartContainer.highcharts(chartOptions);

        this.chart = this.chartContainer.highcharts();

        (Highcharts as any).SVGRenderer.prototype.symbols.cross = function (x, y, w, h) {
            return ["M", x, y, "L", x + w, y + h, "M", x + w, y, "L", x, y + h, "z"];
        };
        if ((Highcharts as any).VMLRenderer) {
            (Highcharts as any).VMLRenderer.prototype.symbols.cross = (
                Highcharts as any
            ).SVGRenderer.prototype.symbols.cross;
        }
    }

    private getChartDataAll(
        f: moment.Moment = null,
        t: moment.Moment = null,
        callback: (data: PVDChartDataPoint[]) => void,
    ): void {
        //: use this to retrieve data for chart (made a separate method to enable retrieving past and forecast data conjointly)
        if (!this.mainSite.liveDataManager.latestData) return;

        //: determine if we want forecast or not (ie split the from/to at now, and retrieve past then forecast ONLY IF forecasts available.)

        if (f == null || t == null) {
            let drpData = this.drp.getData();
            if (drpData && drpData.isValid && drpData.hasRange) {
                if (f == null) {
                    f = drpData.from;
                }
                if (t == null) {
                    t = drpData.to;
                }
            }
        }

        if (f == null || t == null) {
            console.warn("PVD Chart WARNING: from or to dates are missing.");
        } else if (!f.isValid() || !t.isValid()) {
            console.warn("PVD Chart WARNING: from or to dates are not valid.");
        } else if (!t.isAfter(f)) {
            console.warn(
                "PVD Chart WARNING: invalid date range ('to' needs to be after 'from'!).",
            );
        } else {
            let ranges: { from: moment.Moment; to: moment.Moment; forecast: boolean }[] = [];
            let now: moment.Moment = moment.utc(); //: NB! this assumes we are (like historically for this chart) working in UTC
            if (now.isBetween(f, t)) {
                ranges.push({ from: f, to: now, forecast: false });
                ranges.push({ from: now, to: t, forecast: true });
            } else if (t.isSameOrBefore(now)) {
                ranges.push({ from: f, to: t, forecast: false });
            } else if (f.isSameOrAfter(now)) {
                ranges.push({ from: f, to: t, forecast: true });
            }

            let allData: PVDChartDataPoint[] = [];
            let dataCallback = (i) => {
                let range = ranges[i];
                this.getChartDataPart(range.from, range.to, range.forecast, (data) => {
                    // console.info("getChartDataAll - RANGE DATA RETRIEVED", i, data.length, { myData: data, from: range.from.format(DateRangePicker.MOMENT_DEBUG_FORMAT), to: range.to.format(DateRangePicker.MOMENT_DEBUG_FORMAT), forecast: range.forecast, range: range });
                    allData = [].concat(allData, data);

                    if (i == ranges.length - 1) {
                        // console.info("getChartDataAll - ALL DATA RETRIEVED (TODO: UPDATE CHART)", i, allData.length, allData);

                        allData.sort((a, b) => {
                            return a.timestamp - b.timestamp;
                        });

                        //this.applyChartData(allData);
                        if ($.isFunction(callback)) {
                            callback(allData);
                        }
                    } else {
                        i++;
                        dataCallback(i);
                    }
                });
            };

            dataCallback(0);
        }
    }

    private getChartDataPart(
        fromMoment: moment.Moment,
        toMoment: moment.Moment,
        forecast: boolean,
        callback: (data: PVDChartDataPoint[]) => void,
    ): void {
        if (!this.mainSite.liveDataManager.latestData) return;

        const useMcpAi =
            forecast &&
            (this.stationId == "Australia_Prelude" || this.stationId == "Australia_Crux");
        const usePreludeBin08 =
            this.stationId == "Australia_Prelude" && this.sensorId == "RTCP/Currents";

        let speedId =
            useMcpAi || this.currentSpeedUnits == SpeedUnits.mps
                ? ParameterGroupInfoSet.Total_CurrentSpeed_Surface_mps_Mean.Id
                : ParameterGroupInfoSet.Total_CurrentSpeed_Surface_kts_Mean.Id;

        let dirId = ParameterGroupInfoSet.Total_CurrentDirection_Surface_deg_Mean.Id;

        if (forecast && !useMcpAi) {
            speedId = "Forecast_" + speedId;
            dirId = "Forecast_" + dirId;
        }

        if (usePreludeBin08) {
            speedId = "Total_CurrentSpeed_Bin08_mps_Mean";
            dirId = "Total_CurrentDirection_Bin08_deg_Mean";
        }

        // MCP-AI and Prelude Bin08 are raw series in m/s; forecast sensors have no sensor-specific group series.
        const rawSensorId = useMcpAi
            ? "Forecast/Currents/MCP_AI_v1"
            : usePreludeBin08
              ? "RTCP/Currents"
              : null;
        const fetchData = (
            paramId: string,
            cb: (err: any, data: InfluxDataWrapper) => any,
        ): JQueryXHR =>
            rawSensorId
                ? getHistoricData(
                      "P",
                      this.stationId,
                      rawSensorId,
                      paramId,
                      null,
                      fromMoment.valueOf(),
                      toMoment.valueOf(),
                      cb,
                      -1,
                  )
                : getGroupData(
                      this.stationId,
                      paramId,
                      fromMoment.valueOf(),
                      toMoment.valueOf(),
                      cb,
                      this.sensorId ?? undefined,
                  );

        var anyData = false;
        let currentData: PVDChartDataPoint[] = [];
        let desiredPoints = 5000;

        fetchData(speedId, (err: any, data: InfluxDataWrapper) => {
            if (err) {
                console.warn("PVD - Error getting data: ", err);
                this.chart.showLoading("Error getting data...");
                return;
            }

            anyData = anyData || data.Data !== null;

            if (data.Data !== null) {
                data.Data.unshift([fromMoment.valueOf(), null]);
                data.Data.push([toMoment.valueOf(), null]);
            }

            let pointSkip = Math.floor(data.Data.length / desiredPoints);

            data.Data.forEach((dataPoint, i) => {
                if (pointSkip !== 0 && i % pointSkip !== 0) {
                    return;
                }
                let ts = dataPoint[0];
                let val = dataPoint[1];

                if (!rawSensorId && this.currentSpeedUnits == SpeedUnits.kts) {
                    val *= 0.514444;
                }

                if (val) {
                    currentData.push({ timestamp: ts, speed: val, direction: null });
                }
            });
        }).then(() => {
            fetchData(dirId, (err: any, data: InfluxDataWrapper) => {
                if (err) {
                    console.warn("PVD - Error getting data: ", err);
                    this.chart.showLoading("Error getting data...");
                    return;
                }

                anyData = anyData || data.Data !== null;

                if (data.Data !== null) {
                    data.Data.unshift([fromMoment.valueOf(), null]);
                    data.Data.push([toMoment.valueOf(), null]);
                }

                let pointSkip = Math.floor(data.Data.length / desiredPoints);

                data.Data.forEach((dataPoint, i) => {
                    if (pointSkip !== 0 && i % pointSkip !== 0) {
                        return;
                    }
                    let ts = dataPoint[0];
                    let val = dataPoint[1];

                    let x = currentData.filter((x) => x.timestamp === ts);

                    if (x.length) {
                        x[0].direction = val;
                    }
                });
            }).done(() => {
                currentData = currentData.filter((x) => x.speed && x.direction); //: NB: sorting has been moved to the all data fetching wrapper, so data from multiple sets is properly sorted.

                if ($.isFunction(callback)) {
                    callback(currentData);
                }
            });
        });
    }

    private applyChartData(data: PVDChartDataPoint[]): void {
        if (!data.length) {
            this.chart.showLoading("Data not available");
            return;
        }

        let axisMin: number = null;
        let axisMax: number = null;
        let axesSet: boolean = this.$ckboxScales.prop("checked");

        this.scaleMin = null;
        this.scaleMax = null;

        let opts: PVDSpeedOptions = this.lsOptsGet();

        let speedMin: number =
            opts.min !== null
                ? opts.min
                : data.reduce((prev, curr) => {
                      return curr.speed <= prev.speed ? curr : prev;
                  }).speed;
        let speedMax: number =
            opts.max !== null
                ? opts.max
                : data.reduce((prev, curr) => {
                      return curr.speed >= prev.speed ? curr : prev;
                  }).speed;

        let gradient = this.getGradient(true, speedMin, speedMax);
        this.setSpeedLegend(speedMin, speedMax, opts.reversed, opts.textGranularity); // (speedMax >= 1.3 ? 2 : 1)

        //draw chart
        let pvdData: { x: number; y: number; color: string; name: string; speed: number }[] = [];
        let datePoints: { x: number; y: number; name: string }[] = [];

        let pointX: number;
        let pointY: number;
        let lastTs;
        let lastDate = null;

        let tzn: string = "UTC";
        let tzd: ITimeZoneData = this.drp.getSelectedTimezoneData();
        if (tzd) {
            tzn = tzd.timeZoneName;
        }

        data.forEach((d) => {
            if (!lastTs) {
                pointX = 0;
                pointY = 0;
            } else {
                let r = (((d.timestamp - lastTs) / 1000) * d.speed) / 1000;
                let theta = d.direction * (Math.PI / 180);
                pointX = pointX + r * Math.sin(theta);
                pointY = pointY + r * Math.cos(theta);
            }

            let date = moment.unix(d.timestamp / 1000).format("DD-MM");
            if (date !== lastDate) {
                datePoints.push({ x: pointX, y: pointY, name: date });
            }

            lastTs = d.timestamp;
            lastDate = date;

            let closest = gradient.reduce((prev, curr) => {
                return Math.abs(curr.speed - d.speed) < Math.abs(prev.speed - d.speed)
                    ? curr
                    : prev;
            });

            //: set scaling min/max
            if (this.scaleMin == null || this.scaleMin > Math.min(pointX, pointY)) {
                this.scaleMin = Math.min(pointX, pointY);
            }

            if (this.scaleMax == null || this.scaleMax < Math.max(pointX, pointY)) {
                this.scaleMax = Math.max(pointX, pointY);
            }

            pvdData.push({
                x: pointX,
                y: pointY,
                color: closest.color,
                name: moment.utc(d.timestamp).tz(tzn).format("YYYY-MM-DD HH:mm (ZZ)"),
                speed: d.speed,
            });
        });

        this.chart.hideLoading();
        let pvdSeries = {
            id: "PVD",
            name: "PVD",
            data: pvdData, //: NB: this will most likely often cause console to show "Highcharts error #15 (Highcharts expects data to be sorted)", as values along the x axix are not an actual timeseries set of data.
        };

        let dateSeries = {
            id: "DateSeries",
            name: "DateSeries",
            data: datePoints,
            color: "black",
            enableMouseTracking: false,
            marker: {
                radius: 5,
                symbol: "cross",
                lineColor: null,
                lineWidth: 2,
            },
            dataLabels: {
                format: "{point.name}",
                enabled: true,
            },
        };

        if (this.chart.series.length < 1) {
            this.chart.addSeries(pvdSeries);
            this.chart.addSeries(dateSeries);
        } else {
            let cs: __Highcharts.SeriesObject = <__Highcharts.SeriesObject>this.chart.get("PVD");
            cs.setData(<any>pvdSeries.data);

            cs = <__Highcharts.SeriesObject>this.chart.get("DateSeries");
            cs.setData(<any>dateSeries.data);
        }

        if (axesSet) {
            axisMin = this.scaleMin;
            axisMax = this.scaleMax;
        }
        // console.info("AXES SET, DATA received", { count: pvdData.length, set: axesSet, min: axisMin, max: axisMax, sMin: this.scaleMin, sMax: this.scaleMax });
        this.updateChartAxes(axisMin, axisMax);
        this.chart.reflow();
        this.legendContainer.show();
    }

    private updateChartHours(hours: number, reload: boolean, forecast: boolean): void {
        let toMoment: moment.Moment;
        let fromMoment: moment.Moment;
        let tzd: ITimeZoneData = this.drp.getSelectedTimezoneData();
        let tzn: string = "UTC";
        if (tzd) {
            tzn = tzd.timeZoneName;
        }

        if (forecast) {
            fromMoment = moment.utc().tz(tzn);
            toMoment = moment.utc().tz(tzn).add(hours, "h");
        } else {
            toMoment = moment.utc().tz(tzn);
            fromMoment = moment.utc().tz(tzn).subtract(hours, "h");
        }

        this.drp.setValues(fromMoment, toMoment, true, "PVD.updateChartHours");
    }

    private updateChart(): void {
        let range = this.drp.getData();
        if (range && range.isValid && range.hasRange && this.mainSite.liveDataManager.latestData) {
            this.chart.showLoading("Getting data...");

            this.getChartDataAll(range.from, range.to, (data) => {
                this.applyChartData(data);
            });
        }
    }

    private updateChartAxes(min: number = null, max: number = null): void {
        //: NB: keep as null to reset the extremes on the axes! (https://stackoverflow.com/a/27062784/753113)
        // console.info("chart scales - updateChartAxes().. [START]", { min: min, max: max, sMin: this.scaleMin, sMax: this.scaleMax });
        try {
            this.chart.showLoading("Updating chart scale...");

            this.chart.yAxis.forEach((axis) => {
                axis.setExtremes(min, max, false, false);
            });
            this.chart.xAxis.forEach((axis) => {
                axis.setExtremes(min, max, true, false);
            });

            this.chart.hideLoading();

            // console.info("chart scales - updateChartAxes().. [FINISHED]", { min: min, max: max, sMin: this.scaleMin, sMax: this.scaleMax });
        } catch (err) {
            console.warn("ERROR: unable to update chart axes: " + err.message, {
                err: err,
                chart: this.chart,
                min: min,
                max: max,
                sMin: this.scaleMin,
                sMax: this.scaleMax,
            });
        }
    }

    public autoSetChartAxes(): void {
        // console.info("chart scales - autoSetChartAxes()..");
        try {
            let axisMin: number = null; //: NB: keep as null to reset the extremes on the axes! (https://stackoverflow.com/a/27062784/753113)
            let axisMax: number = null;

            if (this.$ckboxScales.prop("checked")) {
                axisMin = this.scaleMin;
                axisMax = this.scaleMax;
            }

            //: now set:
            this.updateChartAxes(axisMin, axisMax);
        } catch (err) {
            console.warn("ERROR while automatically setting chart axes: ", err);
        }
    }

    private setSquareChart(square: boolean = true, reflow: boolean = true): void {
        try {
            if (square) {
                let w: number = this.chartContainer.outerWidth();
                this.chartContainer.css("height", w + "px"); //.css('border', '2px red dashed');
            } else {
                this.chartContainer.css("height", this.originalContainerHeightPercent + "%"); //.css('border', '2px green dashed');
            }

            if (reflow) {
                this.chart.reflow();
            }
        } catch (err) {
            console.warn("ERROR while automatically setting chart square (or not): ", {
                err: err,
                square: square,
                reflow: reflow,
            });
        }
    }

    private setSpeedLegend(
        min: number = null,
        max: number = null,
        reversed: boolean = true,
        textGranularity: number = null,
    ): void {
        this.$speedLegendUL.empty();

        let gradient = this.getGradient(true, min, max);
        if (reversed) {
            gradient.reverse();
        }
        const w: number = 100 / gradient.length;
        // const textGranularity: number = 1;
        if (textGranularity == null) {
            textGranularity = Math.max(1, Math.ceil(max));
        }

        gradient.forEach((g) => {
            this.$speedLegendUL.append(
                $("<li></li>")
                    .css("width", w + "%")
                    .append($('<div class="heatmap_top"></div>').css("backgroundColor", g.color))
                    .append(
                        $('<div class="heatmap_bottom"></div>').text(
                            (g.speed * 10) % textGranularity === 0 ? g.speed : "",
                        ),
                    ),
            );
        });
    }

    private formatHoursToDays(hours: number): string {
        let days = hours / 24;
        if (days === 1) {
            return days + " day";
        }
        return days + " days";
    }

    private hex(c): string {
        //: https://stackoverflow.com/a/32257791/753113
        var s = "0123456789abcdef";
        var i = parseInt(c);
        if (i == 0 || isNaN(c)) return "00";
        i = Math.round(Math.min(Math.max(0, i), 255));
        return s.charAt((i - (i % 16)) / 16) + s.charAt(i % 16);
    }

    private convertToHex(rgb: number[]): string {
        //: https://stackoverflow.com/a/32257791/753113 (Convert an RGB triplet to a hex string)
        return this.hex(rgb[0]) + this.hex(rgb[1]) + this.hex(rgb[2]);
    }

    private trim(s: string): string {
        return s.charAt(0) == "#" ? s.substring(1, 7) : s;
    } //: https://stackoverflow.com/a/32257791/753113 (remove '#' in hex color string)

    private convertToRGB(hex: string): number[] {
        //: https://stackoverflow.com/a/32257791/753113 (Convert a hex string to an RGB triplet)
        var color = [];
        color[0] = parseInt(this.trim(hex).substring(0, 2), 16);
        color[1] = parseInt(this.trim(hex).substring(2, 4), 16);
        color[2] = parseInt(this.trim(hex).substring(4, 6), 16);
        return color;
    }

    private generateColor(colorStart, colorEnd, colorCount) {
        //: https://stackoverflow.com/a/32257791/753113 (Convert a hex string to an RGB triplet)
        // The beginning of your gradient
        var start = this.convertToRGB(colorStart);

        // The end of your gradient
        var end = this.convertToRGB(colorEnd);

        // The number of colors to compute
        var len = colorCount;

        //Alpha blending amount
        var alpha = 0.0;

        var saida: string[] = [];

        for (let i = 0; i < len; i++) {
            var c = [];
            alpha += 1.0 / len;

            c[0] = start[0] * alpha + (1 - alpha) * end[0];
            c[1] = start[1] * alpha + (1 - alpha) * end[1];
            c[2] = start[2] * alpha + (1 - alpha) * end[2];

            saida.push("#" + this.convertToHex(c));
        }

        return saida;
    }

    private getGradient(
        autogen: boolean = false,
        min: number = null,
        max: number = null,
        interval: number = 0.0125,
    ): PVDGradientEntry[] {
        if (autogen && min !== null && max !== null) {
            let out: PVDGradientEntry[] = [];
            let arr: number[] = [];
            let n: number = min;
            while (n <= max) {
                arr.push(n);
                // n = +(n + interval).toFixed(4); //: https://stackoverflow.com/a/12830454
                n = +(Math.ceil((n + interval) / interval) * interval).toFixed(4); //: https://stackoverflow.com/a/12830454  &&  https://stackoverflow.com/a/18953446/753113
            }
            const count: number = arr.length; // Math.floor(max / interval) - Math.floor((min - 1) / interval);

            let colours: string[] = [];
            //: split over 4 colours, to get a nicer gradient and closer to previous colours: colour stops: ['#3E26A8', '#2472BA', '#20CF96', '#36E41B', '#ECF915'] = [0.0, 0.2, 0.4, 0.6, 0.8]
            // NOTE: an alternative would be to try using an external library such as https://www.npmjs.com/package/gradstop, which seems to work nicely too. test (NB: only supports up to 4 colours in colorArray!): gradstop({ stops: 65, inputFormat: 'hex', colorArray: ['#3E26A8', '#2472BA', '#36E41B', '#ECF915'] }).forEach(x => { console.info(`%c       ` + `%c ${x}`, `background: ${x}`, `background: #fff, color: ${x}`); });
            colours = colours.concat(
                this.generateColor("#3E26A8", "#2472BA", Math.floor(count / 4)).reverse(),
            ); //: 1: static 0.0 - 0.2
            colours = colours.concat(
                this.generateColor("#2472BA", "#20CF96", Math.floor(count / 4)).reverse(),
            ); //: 2: static 0.2 - 0.4
            colours = colours.concat(
                this.generateColor("#20CF96", "#36E41B", Math.floor(count / 4)).reverse(),
            ); //: 3: static 0.4 - 0.6
            colours = colours.concat(
                this.generateColor(
                    "#36E41B",
                    "#ECF915",
                    count - Math.floor(count / 4) * 4 + Math.floor(count / 4),
                ).reverse(),
            ); //: 4: static 0.6 - 0.8

            arr.forEach((n, i) => {
                out.push({ speed: n, color: colours[i] });
            });

            /*
            console.groupCollapsed("GRADIENT COLOURS PREVIEWS (COUNT: " + count + ")");
            console.info("getGradient..", { interval: interval, count: count, min: min, max: max, out: out, arr: arr, colours: colours });
            colours.forEach(x => {
                console.info(`%c       ` + `%c ${x}`, `background: ${x}`, `background: #fff, color: ${x}`);
            });
            console.groupEnd();
            */

            return out;
        }

        return [
            { speed: 0.8, color: "#ECF915" },
            { speed: 0.7875, color: "#DFF815" },
            { speed: 0.775, color: "#D3F716" },
            { speed: 0.7625, color: "#C6F516" },
            { speed: 0.75, color: "#BAF417" },
            { speed: 0.7375, color: "#AEF317" },
            { speed: 0.725, color: "#A3F118" },
            { speed: 0.7125, color: "#97F018" },
            { speed: 0.7, color: "#8CEF18" },
            { speed: 0.6875, color: "#80ED19" },
            { speed: 0.675, color: "#75EC19" },
            { speed: 0.6625, color: "#6AEB1A" },
            { speed: 0.65, color: "#60E91A" },
            { speed: 0.6375, color: "#55E81A" },
            { speed: 0.625, color: "#4BE71B" },
            { speed: 0.6125, color: "#40E51B" },
            { speed: 0.6, color: "#36E41B" },
            { speed: 0.5875, color: "#2CE31C" },
            { speed: 0.575, color: "#23E11C" },
            { speed: 0.5625, color: "#1CE020" },
            { speed: 0.55, color: "#1DDF2A" },
            { speed: 0.5375, color: "#1DDE34" },
            { speed: 0.525, color: "#1DDC3E" },
            { speed: 0.5125, color: "#1EDB47" },
            { speed: 0.5, color: "#1EDA51" },
            { speed: 0.4875, color: "#1ED85A" },
            { speed: 0.475, color: "#1FD763" },
            { speed: 0.4625, color: "#1FD66C" },
            { speed: 0.45, color: "#1FD474" },
            { speed: 0.4375, color: "#20D37D" },
            { speed: 0.425, color: "#20D285" },
            { speed: 0.4125, color: "#20D08E" },
            { speed: 0.4, color: "#20CF96" },
            { speed: 0.3875, color: "#21CE9E" },
            { speed: 0.375, color: "#21CCA5" },
            { speed: 0.3625, color: "#21CBAD" },
            { speed: 0.35, color: "#21CAB5" },
            { speed: 0.3375, color: "#22C8BC" },
            { speed: 0.325, color: "#22C7C3" },
            { speed: 0.3125, color: "#22C2C6" },
            { speed: 0.3, color: "#22B8C4" },
            { speed: 0.2875, color: "#22AFC3" },
            { speed: 0.275, color: "#23A6C2" },
            { speed: 0.2625, color: "#239DC1" },
            { speed: 0.25, color: "#2394BF" },
            { speed: 0.2375, color: "#238BBE" },
            { speed: 0.225, color: "#2383BD" },
            { speed: 0.2125, color: "#247ABB" },
            { speed: 0.2, color: "#2472BA" },
            { speed: 0.1875, color: "#246AB9" },
            { speed: 0.175, color: "#2462B7" },
            { speed: 0.1625, color: "#245AB6" },
            { speed: 0.15, color: "#2452B5" },
            { speed: 0.1375, color: "#254BB3" },
            { speed: 0.125, color: "#2544B2" },
            { speed: 0.1125, color: "#253CB1" },
            { speed: 0.1, color: "#2535AF" },
            { speed: 0.0875, color: "#252EAE" },
            { speed: 0.075, color: "#2527AD" },
            { speed: 0.0625, color: "#2A25AB" },
            { speed: 0.05, color: "#3025AA" },
            { speed: 0.0375, color: "#3725A9" },
            { speed: 0.025, color: "#3E26A8" },
            { speed: 0.0125, color: "#3E26A8" },
            { speed: 0.0, color: "#3E26A8" },
        ];
    }

    private lsOptsGet(): PVDSpeedOptions {
        let x: PVDSpeedOptions = {
            min: null,
            max: null,
            reversed: true,
            textGranularity: null,
        };

        let ls: string = $.cookie(this.lsOptsKey);
        if (ls) {
            try {
                let parsed: PVDSpeedOptions = JSON.parse(ls) as PVDSpeedOptions;
                if (parsed) {
                    x = parsed;
                }
            } catch (err) {
                console.warn("Unable to parse locally stored PVD Chart Options..", {
                    err: err,
                    ls: ls,
                });
            }
        }

        return x;
    }

    private lsOptsSet(x: PVDSpeedOptions, redrawChart: boolean = true): void {
        let s: string = JSON.stringify(x);
        $.cookie(this.lsOptsKey, s, { expires: 5 * 365, path: "/" });

        if (redrawChart) {
            // this.updateChartHours(this.selectedHours, true, this.forecast);
            this.updateChart();
        }
    }

    private showSpeedOptionsModal() {
        let opts: PVDSpeedOptions = this.lsOptsGet();
        let sd = new SimpleDialog(`PVD Chart Speed Range`, null);
        let $body: JQuery = sd.getBody();
        let $foot: JQuery = sd.getFooter();

        $body.empty();

        let $form: JQuery = $("<form>").addClass("form-horizontal").appendTo($body);
        let $group: JQuery = null;
        let $check_label: JQuery = null;
        let $inputMinVal: JQuery = null;
        let $inputMaxVal: JQuery = null;
        let $inputMinCheck: JQuery = null;
        let $inputMaxCheck: JQuery = null;
        let $inputReversedCheck: JQuery = null;
        let $inputGranularityVal: JQuery = null;

        if (1 == 1) {
            //: code wrapper for inputs setup..
            //: min:
            $group = $("<div>").addClass("form-group").appendTo($form);
            $("<label>").addClass("col-sm-2 control-label").text("Minimum:").appendTo($group);
            $inputMinVal = $(
                '<input type="text" style="text-align:right;" name="min" placeholder="Minimum">',
            ).addClass("input-sm form-control");
            $inputMinVal.appendTo($('<div class="col-sm-5">').appendTo($group));
            $check_label = $("<label>")
                .addClass("checkbox inline")
                .appendTo($('<div class="col-sm-5">').appendTo($group));
            $inputMinCheck = $('<input type="checkbox" name="min_check">').appendTo($check_label);
            $check_label.append("<span>Auto</span>");

            $inputMinCheck.on("change", () => {
                if ($inputMinCheck.is(":checked")) {
                    $inputMinVal.val("").prop("disabled", true);
                } else {
                    $inputMinVal.prop("disabled", false);
                }
            });

            //: max:
            $group = $("<div>").addClass("form-group").appendTo($form);
            $("<label>").addClass("col-sm-2 control-label").text("Maximum:").appendTo($group);
            $inputMaxVal = $(
                '<input type="text" style="text-align:right;" name="max" placeholder="Maximum">',
            ).addClass("input-sm form-control");
            $inputMaxVal.appendTo($('<div class="col-sm-5">').appendTo($group));
            $check_label = $("<label>")
                .addClass("checkbox inline")
                .appendTo($('<div class="col-sm-5">').appendTo($group));
            $inputMaxCheck = $('<input type="checkbox" name="max_check">').appendTo($check_label);
            $check_label.append("<span>Auto</span>");

            $inputMaxCheck.on("change", () => {
                if ($inputMaxCheck.is(":checked")) {
                    $inputMaxVal.val("").prop("disabled", true);
                } else {
                    $inputMaxVal.prop("disabled", false);
                }
            });

            /*
            //: reversed:
            $group = $("<div>").addClass("form-group").appendTo($form);
            $("<label>").addClass("col-sm-2 control-label").text("Reversed:").appendTo($group);
            $check_label = $("<label>").addClass("checkbox inline").appendTo($("<div class=\"col-sm-10\">").appendTo($group));
            $inputReversedCheck = $("<input type=\"checkbox\" name=\"reversed\">").appendTo($check_label);
            $check_label.append("<span>Reversed (default: checked)</span>");

            //: text granularity:
            let selectOptions: number[] = [1, 2, 3];
            $group = $("<div>").addClass("form-group").appendTo($form);
            $("<label>").addClass("col-sm-2 control-label").text("Labels amount:").appendTo($group);
            $inputGranularityVal = $("<select style=\"text-align:right;\" name=\"textGranularity\">").addClass("input-sm form-control");
            selectOptions.forEach(n => {
                $inputGranularityVal.append(`<option value="${n}">${n}</option>`);
            });
            $inputGranularityVal.appendTo($("<div class=\"col-sm-3\">").appendTo($group));
            $("<em>").addClass("col-sm-7 text-muted").text("Set the granularity of labels on the Current Speed Legend.").appendTo($group);
            */

            //: set values of inputs:
            $inputMinCheck.prop("checked", true).trigger("change");
            $inputMaxCheck.prop("checked", true).trigger("change");
            if (opts !== null && "min" in opts && opts.min !== null) {
                $inputMinCheck.prop("checked", false).trigger("change");
                $inputMinVal.val(opts.min.toString());
            }
            if (opts !== null && "max" in opts && opts.max !== null) {
                $inputMaxCheck.prop("checked", false).trigger("change");
                $inputMaxVal.val(opts.max.toString());
            }
            /*
            if (opts !== null && "reversed" in opts && opts.reversed !== null) {
                $inputReversedCheck.prop("checked", opts.reversed);
            }
            if (opts !== null && "textGranularity" in opts && opts.textGranularity !== null) {
                $inputGranularityVal.val((opts.textGranularity).toString());
            }
            */

            $("<div>")
                .addClass("alert alert-info")
                .html(
                    "<strong>Note:</strong> The default values prior to these customisation options were:<br><u>min</u> = <strong>0</strong><br><u>max</u> = <strong>0.8</strong>.",
                )
                .css("marginBottom", 0)
                .appendTo($form);
        }

        sd.getDismissButton().text("Cancel");

        let $save: JQuery = $("<button>")
            .addClass("btn btn-success")
            .text("Save")
            .prependTo($foot);
        $save.on("click", (e) => {
            let x: PVDSpeedOptions = {
                min: null,
                max: null,
                reversed: true,
                textGranularity: null,
            };

            if (
                $inputMinCheck.prop("checked") == false &&
                !isNaN(parseFloat($inputMinVal.val()))
            ) {
                x.min = parseFloat($inputMinVal.val());
            }

            if (
                $inputMaxCheck.prop("checked") == false &&
                !isNaN(parseFloat($inputMaxVal.val()))
            ) {
                x.max = parseFloat($inputMaxVal.val());
            }

            /*
            x.reversed = $inputReversedCheck.prop('checked');
            x.textGranularity = $inputGranularityVal.val();
            */

            this.lsOptsSet(x, true);

            sd.hide();
        });

        let $reset: JQuery = $("<button>")
            .addClass("btn btn-link pull-left")
            .text("Reset")
            .prependTo($foot);
        $reset.on("click", (e) => {
            $.removeCookie(this.lsOptsKey);
            sd.hide();
            this.updateChart();
        });

        sd.show();
    }
}
